import { i3Assets } from '../utils/i3Assets';
import type { I3EventsPageCms, I3ServicesCms, I3SiteContentV1 } from './types';

/**
 * Default Services page: pillar groupings + one card per bookable offering.
 * Replace each bookUrl (and optional payUrl) in CMS with real scheduling / payment links per service.
 */
export const DEFAULT_SERVICES_CMS: I3ServicesCms = {
  pageSubtitle:
    'Choose a program, read the details, and book the path that fits — each service has its own scheduling link.',
  pillars: [
    {
      id: 'marriage-enrichment',
      title: 'Marriage enrichment',
      subtitle:
        'Counseling and coaching for couples at every stage. Start with the offering that matches where you are today.',
      hidePillarHeading: true,
      cards: [
        {
          id: 'premarital-counseling',
          title: 'Pre-marital counseling',
          whoItsFor: 'Engaged couples preparing for marriage',
          summary:
            'Build a strong foundation before your wedding — expectations, communication, faith, and practical tools for your first years together.',
          outcomes: ['Shared expectations', 'Communication habits', 'Conflict plan you both trust'],
          detailHtml: `<p class="i3-modal-lead">Prepare for marriage with guided conversations that go deeper than a checklist. We help you align on values, roles, money, family, and faith — so you enter marriage as a team.</p>
<section class="i3-modal-section" aria-label="What to expect"><h3 class="i3-modal-section-title">What to expect</h3><ul class="i3-modal-list"><li>Sessions tailored to your story and timeline before the wedding</li><li>Tools for listening, repair, and decision-making together</li><li>Optional integration with your officiant or church requirements</li></ul></section>`,
          bookUrl: 'https://calendly.com/reamathis18/pre-marital-counseling',
          payUrl: i3Assets.cloverPaymentUrl,
        },
        {
          id: 'postmarital-counseling',
          title: 'Post-marital counseling',
          whoItsFor: 'Married couples navigating strain, transition, or healing',
          summary:
            'A structured, compassionate space to rebuild trust, improve communication, and decide on next steps with clarity.',
          outcomes: ['Safer conversations', 'Clearer boundaries', 'Hope-centered plan'],
          detailHtml: `<p class="i3-modal-lead">When marriage feels stuck or painful, you do not have to figure it out alone. We focus on honest dialogue, healthy patterns, and practical steps — honoring your faith and your future.</p>
<section class="i3-modal-section" aria-label="Good fit if"><h3 class="i3-modal-section-title">Good fit if</h3><ul class="i3-modal-list"><li>Conflict repeats without resolution</li><li>Trust has been damaged or intimacy has faded</li><li>You want professional support before making big decisions</li></ul></section>`,
          bookUrl: 'https://calendly.com/reamathis18/post-marital-counseling',
          payUrl: i3Assets.cloverPaymentUrl,
        },
        {
          id: 'counseling-1on1',
          title: '1:1 counseling',
          whoItsFor: 'Individuals seeking private support (often alongside couple work)',
          summary:
            'Personalized sessions for stress, identity, grief, leadership pressure, or growth — integrated with I-3’s faith-centered approach.',
          outcomes: ['Confidential space', 'Actionable insight', 'Alignment with your values'],
          detailHtml: `<p class="i3-modal-lead">One-on-one counseling gives you room to process, heal, and grow at your own pace. Some clients pair this with marriage work; others begin here when individual support is the right first step.</p>
<section class="i3-modal-section" aria-label="Topics"><h3 class="i3-modal-section-title">Common topics</h3><ul class="i3-modal-list"><li>Anxiety, burnout, or major life change</li><li>Preparing for difficult conversations at home</li><li>Leadership and calling outside the home</li></ul></section>`,
          bookUrl: 'https://calendly.com/reamathis18/0ne-on-one',
          payUrl: i3Assets.cloverPaymentUrl,
        },
        {
          id: 'speaking-engagements',
          title: 'Speaking engagements',
          whoItsFor: 'Churches, teams, and events seeking a faith-centered voice',
          summary:
            'Keynotes, panels, and workshops on marriage, leadership, and purpose — tailored to your audience and time slot.',
          outcomes: ['Clear message', 'Engaged audience', 'Actionable takeaways'],
          detailHtml: `<p class="i3-modal-lead">Invite I-3 to bring practical encouragement and biblical grounding to your retreat, conference, staff day, or community gathering. We collaborate on theme, length, and format — from 20-minute segments to half-day workshops.</p>
<section class="i3-modal-section" aria-label="Typical topics"><h3 class="i3-modal-section-title">Typical topics</h3><ul class="i3-modal-list"><li>Strengthening marriage in busy seasons</li><li>Leadership with integrity under pressure</li><li>Faith and mental health in community</li></ul></section>
<p>Reach out with your date, venue, and audience; we&apos;ll follow up with availability and next steps.</p>`,
          bookUrl: '',
        },
      ],
    },
  ],
};

/** Events page — latest experience, hero carousel, media strips, upcoming slots (edit in site-content.json). */
export const DEFAULT_EVENTS_PAGE: I3EventsPageCms = {
  pageSubtitle: 'Workshops, retreats, and moments from the I-3 community.',
  latestHighlight: {
    id: 'fanning-the-flame-latest',
    label: 'Latest experience',
    title: 'Fanning the Flame Marriage Retreat',
    dateLine: 'March 2026 · Indianapolis',
    descriptionHtml: `<p>Fanning the Flame brought couples together for teaching, reflection, and community—right here in Indianapolis. Use the carousel and galleries to see the room, the people, and moments from the day.</p>
<p>Longer reflections and announcements will also live on the <a href="/blog">Journal</a> when you publish them.</p>`,
    carouselSlides: [
      {
        type: 'image',
        src: '/i3/i3picture1.JPG',
        caption: 'Group session in progress — connection, focus, and practical tools.',
        title: 'Workshop in session',
      },
      {
        type: 'image',
        src: '/i3/i3picture2.JPG',
        caption: 'Celebrating together — a snapshot from the retreat.',
        title: 'Fanning the Flame moment',
      },
      {
        type: 'video',
        src: '/i3/i3video1.MP4',
        poster: '/i3/i3picture1.JPG',
        title: 'Highlight clip 1',
      },
      {
        type: 'video',
        src: '/i3/i3video2.MP4',
        poster: '/i3/i3picture2.JPG',
        title: 'Highlight clip 2',
      },
    ],
    videos: [
      { type: 'video', src: '/i3/i3video1.MP4', title: 'Highlight clip 1' },
      { type: 'video', src: '/i3/i3video2.MP4', title: 'Highlight clip 2' },
    ],
    photos: [],
  },
  upcomingSectionTitle: 'Up next',
  upcomingEvents: [
    {
      id: 'couples-bootcamp-2026-09-19',
      title: 'Building the Body & Strengthening the Soul Couples Boot Camp',
      date: 'Saturday, September 19, 2026',
      time: '9:00am – 12:00pm',
      location: 'Location coming soon',
      descriptionHtml:
        '<p><strong>Save the date!</strong> A married couples boot camp focused on building the body and strengthening the soul. Registration and a promo video are on the way — we’ll share the online signup details here as soon as they’re ready.</p>',
      ctaLabel: 'Save the date — contact us',
    },
  ],
  upcomingEmptyHtml: `<p><strong>No upcoming dates listed yet.</strong> When you&apos;re ready to announce the next workshop or retreat, we&apos;ll add it here—fastest path is to reach out with your date and details so we can publish it for you.</p>
<p>In the meantime, follow the <a href="/blog">Journal</a> for encouragement and updates.</p>`,
  homeSpotlight: {
    image: '/i3/i3picture2.JPG',
    headline: 'From our latest retreat',
    subline:
      'A snapshot from Fanning the Flame — see photos, clips, and upcoming dates on the Events page.',
    linkLabel: 'View events & highlights',
    linkTo: '/events',
  },
};

/** Fallback when CMS JSON fetch fails; mirrors public/cms/i3/site-content.json */
export const DEFAULT_SITE_CONTENT: I3SiteContentV1 = {
  version: '1',
  updatedAt: '2026-07-25T00:00:00.000Z',
  featuredEvent: {
    id: 'couples-bootcamp-2026-09-19',
    label: 'bootcamp',
    title: 'Building the Body & Strengthening the Soul Couples Boot Camp',
    year: '2026',
    date: 'Saturday, September 19, 2026',
    time: '9:00am – 12:00pm',
    checkIn: '',
    location: 'Location coming soon',
    description:
      'A married couples boot camp to build the body and strengthen the soul. Save the date — registration details coming soon. Presented by I-3 Coaching & Consulting.',
    registerTypeformUrl: '',
    payCloverUrl: i3Assets.cloverPaymentUrl,
    heroImage: undefined,
  },
  saveTheDate: {
    id: 'couples-bootcamp-2026-09-19',
    enabled: true,
    eyebrow: 'Save the Date',
    title: 'Building the Body & Strengthening the Soul',
    subtitle: 'Couples Boot Camp',
    date: 'Saturday, September 19, 2026',
    time: '9:00am – 12:00pm',
    body: 'Married couples — mark your calendars. Registration and details coming soon.',
    primaryCtaLabel: 'View events',
    primaryCtaTo: '/events',
    dismissLabel: 'Got it',
  },
  eventsPage: DEFAULT_EVENTS_PAGE,
  moreEventsCopy:
    'Additional workshops and retreats will be listed here. Check back or contact us to be notified.',
  pastEventsIntro:
    'Watch highlights and browse photos from our retreats and workshops. Click any video or image to view fullscreen.',
  pastEvents: [
    {
      id: '1',
      title: 'Fanning the Flame Marriage Retreat',
      description:
        'A weekend retreat for couples to strengthen their partnership through guided sessions, reflection, and community.',
      date: 'Mar 2026',
      media: [],
    },
    {
      id: '2',
      title: 'Leadership & Financial Wellness Workshop',
      description:
        'One-day workshop covering leadership development and financial clarity.',
      date: 'TBD',
      media: [],
    },
  ],
  services: DEFAULT_SERVICES_CMS,
  blogPosts: undefined,
  operations: {
    headline: 'I-3 operations (demo data from CMS JSON)',
    featuredEventRegistrationCount: 0,
    totalInquiriesCount: 0,
    notes: 'Replace with live SQL/API counts in production. Edit in public/cms/i3/site-content.json.',
  },
};
